import { useCallback, useEffect, useRef, useState } from 'react'

import type { LabeledBox, NormalizedBox } from './types'

/**
 * The labeling-mode drag gestures, resolved by hit target:
 *
 * - plain drag on a selected box's edge strip → resize that edge
 * - Shift+drag inside the selected box → move it (size preserved)
 * - Shift+drag with Draw Mode armed → draw a new box
 * - Shift+drag otherwise → rubber-band bulk select (always additive)
 * - plain drag anywhere else → panning keeps it
 *
 * Listeners attach natively with mousedown on CAPTURE so a claimed gesture
 * stops propagation before react-zoom-pan-pinch's own bubble listener can
 * start a pan.
 * Move/resize render live through `onLiveBbox` and commit once on mouseup;
 * Escape mid-drag abandons the gesture without committing.
 */

const MIN_DRAW_FRACTION = 0.0025
const MIN_BOX_FRACTION = 0.005
const EDGE_HIT_SCREEN_PX = 6

type GestureKind =
  | 'draw'
  | 'select'
  | 'move'
  | 'resize-n'
  | 'resize-s'
  | 'resize-e'
  | 'resize-w'

interface ActiveGesture {
  kind: GestureKind
  start: { x: number; y: number }
  originalBbox: NormalizedBox | null
}

export interface DraftRect {
  x: number
  y: number
  width: number
  height: number
}

interface UseLabelerGesturesProps {
  svgRef: React.RefObject<SVGSVGElement | null>
  imageNaturalDimensions: { w: number; h: number } | null
  active: boolean
  isDrawingMode: boolean
  selectedBox: LabeledBox | null
  onDraw: (bbox: NormalizedBox) => void
  onRubberBand: (bbox: NormalizedBox) => void
  onCommitBbox: (boxId: string, bbox: NormalizedBox) => void
  onLiveBbox: (live: { boxId: string; bbox: NormalizedBox } | null) => void
  onDraftChange?: (
    draft: { kind: 'draw' | 'select'; rect: DraftRect } | null
  ) => void
}

export function useLabelerGestures({
  svgRef,
  imageNaturalDimensions,
  active,
  isDrawingMode,
  selectedBox,
  onDraw,
  onRubberBand,
  onCommitBbox,
  onLiveBbox,
  onDraftChange,
}: UseLabelerGesturesProps) {
  const [draft, setDraft] = useState<DraftRect | null>(null)
  const gestureRef = useRef<ActiveGesture | null>(null)
  const liveBboxRef = useRef<NormalizedBox | null>(null)

  const propsRef = useRef({
    active,
    isDrawingMode,
    selectedBox,
    imageNaturalDimensions,
    onDraw,
    onRubberBand,
    onCommitBbox,
    onLiveBbox,
    onDraftChange,
  })
  propsRef.current = {
    active,
    isDrawingMode,
    selectedBox,
    imageNaturalDimensions,
    onDraw,
    onRubberBand,
    onCommitBbox,
    onLiveBbox,
    onDraftChange,
  }

  const [isShiftPressed, setIsShiftPressed] = useState(false)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true)
    }
    const clear = (e?: KeyboardEvent) => {
      if (!e || e.key === 'Shift') setIsShiftPressed(false)
    }
    const blur = () => clear()
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', clear)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', clear)
      window.removeEventListener('blur', blur)
    }
  }, [])

  const cancelGesture = useCallback(() => {
    if (!gestureRef.current) return
    gestureRef.current = null
    liveBboxRef.current = null
    setDraft(null)
    propsRef.current.onLiveBbox(null)
    propsRef.current.onDraftChange?.(null)
  }, [])

  const hasImage = !!imageNaturalDimensions?.w
  useEffect(() => {
    const svg = svgRef.current
    if (!svg || !active) return

    const toImage = (clientX: number, clientY: number) => {
      const dims = propsRef.current.imageNaturalDimensions
      if (!dims) return null
      const pt = svg.createSVGPoint()
      pt.x = clientX
      pt.y = clientY
      const ctm = svg.getScreenCTM()
      if (!ctm) return null
      const coords = pt.matrixTransform(ctm.inverse())
      return {
        x: Math.max(0, Math.min(dims.w, coords.x)),
        y: Math.max(0, Math.min(dims.h, coords.y)),
        tolerance: EDGE_HIT_SCREEN_PX / (ctm.a || 1),
      }
    }

    const boxPixelBounds = (bbox: NormalizedBox) => {
      const dims = propsRef.current.imageNaturalDimensions
      if (!dims) return null
      return {
        x0: bbox.x_min * dims.w,
        y0: bbox.y_min * dims.h,
        x1: bbox.x_max * dims.w,
        y1: bbox.y_max * dims.h,
      }
    }

    const handleMouseDown = (e: MouseEvent) => {
      const {
        active: isActive,
        isDrawingMode: drawArmed,
        selectedBox: sel,
      } = propsRef.current
      if (!isActive || e.button !== 0) return
      const focused = document.activeElement
      if (
        focused instanceof HTMLInputElement ||
        focused instanceof HTMLTextAreaElement ||
        focused instanceof HTMLSelectElement
      ) {
        focused.blur()
      }
      const coords = toImage(e.clientX, e.clientY)
      if (!coords) return

      let kind: GestureKind | null = null
      let originalBbox: NormalizedBox | null = null

      if (sel && !drawArmed) {
        const b = boxPixelBounds(sel.bbox)
        if (b) {
          const tol = coords.tolerance
          const withinX = coords.x >= b.x0 - tol && coords.x <= b.x1 + tol
          const withinY = coords.y >= b.y0 - tol && coords.y <= b.y1 + tol
          if (!e.shiftKey && withinX && withinY) {
            if (Math.abs(coords.y - b.y0) <= tol) kind = 'resize-n'
            else if (Math.abs(coords.y - b.y1) <= tol) kind = 'resize-s'
            else if (Math.abs(coords.x - b.x0) <= tol) kind = 'resize-w'
            else if (Math.abs(coords.x - b.x1) <= tol) kind = 'resize-e'
          } else if (
            e.shiftKey &&
            coords.x > b.x0 + tol &&
            coords.x < b.x1 - tol &&
            coords.y > b.y0 + tol &&
            coords.y < b.y1 - tol
          ) {
            kind = 'move'
          }
          if (kind) originalBbox = { ...sel.bbox }
        }
      }

      if (!kind && e.shiftKey) {
        kind = drawArmed ? 'draw' : 'select'
      }
      if (!kind) return

      gestureRef.current = { kind, start: coords, originalBbox }
      if (kind === 'draw' || kind === 'select') {
        const rect = { x: coords.x, y: coords.y, width: 0, height: 0 }
        setDraft(rect)
        propsRef.current.onDraftChange?.({ kind, rect })
      }
      e.preventDefault()
      e.stopPropagation()
    }

    const applyMoveResize = (coords: { x: number; y: number }) => {
      const gesture = gestureRef.current
      const { selectedBox: sel, imageNaturalDimensions: dims } =
        propsRef.current
      if (!gesture?.originalBbox || !sel || !dims) return
      const orig = gesture.originalBbox
      const dx = (coords.x - gesture.start.x) / dims.w
      const dy = (coords.y - gesture.start.y) / dims.h
      let next: NormalizedBox
      if (gesture.kind === 'move') {
        const w = orig.x_max - orig.x_min
        const h = orig.y_max - orig.y_min
        const x = Math.max(0, Math.min(1 - w, orig.x_min + dx))
        const y = Math.max(0, Math.min(1 - h, orig.y_min + dy))
        next = { x_min: x, y_min: y, x_max: x + w, y_max: y + h }
      } else {
        next = { ...orig }
        if (gesture.kind === 'resize-n') {
          next.y_min = Math.min(orig.y_max - MIN_BOX_FRACTION, orig.y_min + dy)
        } else if (gesture.kind === 'resize-s') {
          next.y_max = Math.max(orig.y_min + MIN_BOX_FRACTION, orig.y_max + dy)
        } else if (gesture.kind === 'resize-w') {
          next.x_min = Math.min(orig.x_max - MIN_BOX_FRACTION, orig.x_min + dx)
        } else if (gesture.kind === 'resize-e') {
          next.x_max = Math.max(orig.x_min + MIN_BOX_FRACTION, orig.x_max + dx)
        }
        next.x_min = Math.max(0, next.x_min)
        next.y_min = Math.max(0, next.y_min)
        next.x_max = Math.min(1, next.x_max)
        next.y_max = Math.min(1, next.y_max)
      }
      liveBboxRef.current = next
      propsRef.current.onLiveBbox({ boxId: sel.id, bbox: next })
    }

    const handleMouseMove = (e: MouseEvent) => {
      const gesture = gestureRef.current
      if (!gesture) return
      const coords = toImage(e.clientX, e.clientY)
      if (!coords) return
      if (gesture.kind === 'draw' || gesture.kind === 'select') {
        const rect = {
          x: Math.min(gesture.start.x, coords.x),
          y: Math.min(gesture.start.y, coords.y),
          width: Math.abs(coords.x - gesture.start.x),
          height: Math.abs(coords.y - gesture.start.y),
        }
        setDraft(rect)
        propsRef.current.onDraftChange?.({ kind: gesture.kind, rect })
      } else {
        applyMoveResize(coords)
      }
      e.preventDefault()
    }

    const handleMouseUp = (e: MouseEvent) => {
      const gesture = gestureRef.current
      if (!gesture) return
      const dims = propsRef.current.imageNaturalDimensions
      const coords = toImage(e.clientX, e.clientY)
      gestureRef.current = null
      setDraft(null)
      propsRef.current.onDraftChange?.(null)

      if (gesture.kind === 'draw' || gesture.kind === 'select') {
        if (!coords || !dims) return
        const bbox: NormalizedBox = {
          x_min: Math.min(gesture.start.x, coords.x) / dims.w,
          y_min: Math.min(gesture.start.y, coords.y) / dims.h,
          x_max: Math.max(gesture.start.x, coords.x) / dims.w,
          y_max: Math.max(gesture.start.y, coords.y) / dims.h,
        }
        if (
          bbox.x_max - bbox.x_min < MIN_DRAW_FRACTION ||
          bbox.y_max - bbox.y_min < MIN_DRAW_FRACTION
        ) {
          return
        }
        if (gesture.kind === 'draw') propsRef.current.onDraw(bbox)
        else propsRef.current.onRubberBand(bbox)
      } else {
        const finalBbox = liveBboxRef.current
        const sel = propsRef.current.selectedBox
        liveBboxRef.current = null
        propsRef.current.onLiveBbox(null)
        if (finalBbox && sel) {
          propsRef.current.onCommitBbox(sel.id, finalBbox)
        }
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && gestureRef.current) {
        e.stopPropagation()
        cancelGesture()
      }
    }

    svg.addEventListener('mousedown', handleMouseDown, { capture: true })
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('keydown', handleEscape, { capture: true })
    return () => {
      svg.removeEventListener('mousedown', handleMouseDown, { capture: true })
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('keydown', handleEscape, { capture: true })
      cancelGesture()
    }
  }, [svgRef, active, hasImage, cancelGesture])

  return {
    draft,
    isDrawing: draft !== null,
    isShiftPressed,
  }
}
